import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Paper, Chip, Divider, CircularProgress, Tooltip, Button } from "@mui/material";
import { ChevronLeft as PrevIcon, ChevronRight as NextIcon } from "@mui/icons-material";
import { apiFetch } from "../api";

/**
 * T-Rex (TEM / PED) viewer.
 *
 * What the synced IDEX data actually contains, and therefore what this page
 * shows, read-only:
 *  - exploitation nodes: the PED sites, each with sub-nodes that publish a
 *    weekly availability schedule (start/end per weekday, Zulu), networks,
 *    data formats, expertise and points of contact;
 *  - TEM events: named exercises with a date window, operating areas and the
 *    nodes they engage;
 *  - PED units and mission PED taskings (the latter reference missions by
 *    upstream id and mostly carry no explicit exploitation window).
 */

// ── Types ─────────────────────────────────────────────────────────

interface DayHours { startTime?: string; endTime?: string }
type Weekday = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";
const WEEKDAYS: Weekday[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Subnode = Partial<Record<Weekday, DayHours>> & { _id?: string; name?: string };

interface ExploitationNode {
  _id: string;
  name: string;
  deleted?: boolean;
  nationality?: string;
  timezoneName?: string;
  parentOrg?: { name?: string } | null;
  validDates?: { start?: string; end?: string };
  authorityToCommitPOC?: { name?: string; rank?: string; position?: string };
  pointsOfContact?: { unclassEmail?: string; unclassPhone?: string };
  ITResources?: {
    accessibleNetworks?: Array<{ name: string }>;
    exploitableNetworks?: Array<{ name: string }>;
    exploitableDataFormats?: Array<{ format: string }>;
    uploadDownloadImagery?: string;
    canMoveBetweenNetworks?: boolean;
  };
  capabilities?: {
    intelExpertise?: Array<{ name: string }>;
    geographicalExpertise?: string[];
    sensorCategories?: Array<{ int_discipline: string; sub_categories?: string[] }>;
    exploitationLevels?: Array<{ name: string }>;
    operationalRestrictions?: string;
    technicalRestrictions?: string;
    subnodeCollections?: Array<{ platform?: { name?: string; designator?: string; color?: string }; subnodes?: Subnode[] }>;
  };
}

interface TemEvent {
  _id: string;
  name: string;
  description?: string;
  start?: string;
  stop?: string;
  operating_areas?: string[];
  exploitationNodes?: string[];
  com_unit?: { name?: string };
}

interface PedUnit { _id: string; name: string }

interface MissionPedTasking {
  _id: string;
  missionId: string;
  taskings: Array<{ collectionId: string; exploitationTimesOverride?: { start?: string; stop?: string } }>;
}

// ── Constants ─────────────────────────────────────────────────────

const THEME = { bg: "#12131b", paper: "#1a1c26", border: "#333", accent: "#f72bef", secondary: "#00b8ff", ok: "#00e676", warn: "#ff9100", muted: "#8a90a0", text: "#cacce5" };
const SIDEBAR_WIDTH = 220;
const DAYS = 6;
const DAY_MS = 86_400_000;

// ── Helpers (all Zulu) ────────────────────────────────────────────

function utcMidnight(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function dayLabel(d: Date): string {
  return `${WEEKDAYS[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]}`;
}
function shortDate(iso?: string): string {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function julian(d: Date): number {
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.floor((d.getTime() - start) / DAY_MS) + 1;
}
function hhmm(minutes: number): string {
  if (minutes >= 1440) return "2400";
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}${String(minutes % 60).padStart(2, "0")}`;
}
/** "0200" to minutes; "0000+" means end of day; "" is no coverage */
function hhmmToMinutes(v?: string): number | null {
  if (!v) return null;
  const s = v.trim();
  if (s === "0000+" || s === "2400") return 1440;
  const m = /^(\d{2}):?(\d{2})$/.exec(s);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
function subnodeWindow(sn: Subnode, weekday: Weekday): [number, number] | null {
  const h = sn[weekday];
  if (!h) return null;
  const start = hhmmToMinutes(h.startTime);
  let end = hhmmToMinutes(h.endTime);
  if (start === null || end === null) return null;
  if (end === 1439) end = 1440; // "2359" reads as round-the-clock
  if (end <= start) return null;
  return [start, end];
}
function allSubnodes(node: ExploitationNode): Array<{ sub: Subnode; platform?: string; color?: string }> {
  const out: Array<{ sub: Subnode; platform?: string; color?: string }> = [];
  (node.capabilities?.subnodeCollections || []).forEach((c) => {
    (c.subnodes || []).forEach((sub) => out.push({ sub, platform: c.platform?.name || c.platform?.designator, color: c.platform?.color }));
  });
  return out;
}

export default function Tem() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState<ExploitationNode[]>([]);
  const [events, setEvents] = useState<TemEvent[]>([]);
  const [pedUnits, setPedUnits] = useState<PedUnit[]>([]);
  const [pedTaskings, setPedTaskings] = useState<MissionPedTasking[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  useEffect(() => {
    const parse = async (r: Response): Promise<any[]> => {
      if (!r.ok) return [];
      const body = await r.json();
      return Array.isArray(body) ? body : body?.resultsArray || [];
    };
    (async () => {
      try {
        const [n, e, u, t] = await Promise.all([
          apiFetch("/api/exploitation-nodes"),
          apiFetch("/api/tem-events"),
          apiFetch("/api/ped-units"),
          apiFetch("/api/mission-ped-taskings"),
        ]);
        setNodes(await parse(n));
        setEvents(await parse(e));
        setPedUnits(await parse(u));
        setPedTaskings(await parse(t));
      } catch (err) {
        console.error("Failed to fetch T-Rex data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Window start: URL param, else the most recent TEM event, else today.
  const startDate = useMemo(() => {
    const p = searchParams.get("start");
    if (p && !Number.isNaN(Date.parse(p))) return utcMidnight(new Date(`${p}T00:00:00Z`));
    const dated = events
      .filter((e) => e.start && !Number.isNaN(Date.parse(e.start)))
      .sort((a, b) => Date.parse(b.start as string) - Date.parse(a.start as string));
    if (dated.length) return utcMidnight(new Date(dated[0].start as string));
    return utcMidnight(new Date());
  }, [searchParams, events]);

  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => new Date(startDate.getTime() + i * DAY_MS)), [startDate]);
  const windowStart = startDate.getTime();
  const windowEnd = windowStart + DAYS * DAY_MS;

  const shift = (deltaDays: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("start", isoDay(new Date(startDate.getTime() + deltaDays * DAY_MS)));
    setSearchParams(next);
  };
  const jumpTo = (iso: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start", isoDay(utcMidnight(new Date(iso))));
    setSearchParams(next);
  };

  const orderedNodes = useMemo(
    () => [...nodes].sort((a, b) => Number(!!a.deleted) - Number(!!b.deleted) || a.name.localeCompare(b.name)),
    [nodes]
  );
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n._id, n])), [nodes]);
  const detailNode = selectedNode ? nodeById.get(selectedNode) || null : null;

  const visibleEvents = useMemo(
    () => events.filter((e) => e.start && e.stop && Date.parse(e.stop) >= windowStart && Date.parse(e.start) <= windowEnd),
    [events, windowStart, windowEnd]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress sx={{ color: THEME.secondary }} />
      </Box>
    );
  }

  const label = (text: string) => (
    <Typography sx={{ color: THEME.muted, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.05em", mt: 1, mb: 0.3 }}>{text}</Typography>
  );
  const chips = (items: string[] | undefined, color: string) =>
    items && items.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
        {items.map((s) => (
          <Chip key={s} label={s} size="small" sx={{ height: 18, fontSize: "0.62rem", backgroundColor: `${color}22`, color }} />
        ))}
      </Box>
    ) : (
      <Typography sx={{ color: "#555", fontSize: "0.7rem" }}>none</Typography>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ color: "#e0e0e0", fontWeight: "bold" }}>T-Rex (PED) Timeline</Typography>
        <Chip label="Read-Only Replica" size="small" variant="outlined" sx={{ borderColor: THEME.accent, color: THEME.accent, fontSize: "0.7rem" }} />
        <Typography sx={{ color: THEME.muted, fontSize: "0.75rem" }}>
          {nodes.length} exploitation nodes · {events.length} TEM events · {pedUnits.length} PED units · {pedTaskings.length} mission PED taskings
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" startIcon={<PrevIcon />} onClick={() => shift(-DAYS)} sx={{ color: THEME.secondary, textTransform: "none" }}>Previous 6 days</Button>
          <Typography sx={{ color: THEME.text, fontWeight: 700, fontSize: "0.85rem" }}>
            {dayLabel(days[0])} to {dayLabel(days[DAYS - 1])} {days[0].getUTCFullYear()}
          </Typography>
          <Button size="small" endIcon={<NextIcon />} onClick={() => shift(DAYS)} sx={{ color: THEME.secondary, textTransform: "none" }}>Next 6 days</Button>
        </Box>
      </Box>

      {/* TEM events: jump chips */}
      {events.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ color: THEME.muted, fontSize: "0.7rem", textTransform: "uppercase" }}>TEM events</Typography>
          {events.map((e) => {
            const active = visibleEvents.includes(e);
            return (
              <Tooltip key={e._id} title={`${e.description || ""} ${e.com_unit?.name ? "· " + e.com_unit.name : ""}`.trim()}>
                <Chip
                  label={`${e.name} · ${shortDate(e.start)} to ${shortDate(e.stop)}${e.operating_areas?.length ? " · " + e.operating_areas.join(", ") : ""}`}
                  size="small"
                  onClick={() => e.start && jumpTo(e.start)}
                  sx={{ height: 22, fontSize: "0.68rem", cursor: "pointer", backgroundColor: active ? "rgba(247,43,239,0.2)" : "rgba(255,255,255,0.05)", color: active ? THEME.accent : THEME.text, border: `1px solid ${active ? THEME.accent : THEME.border}` }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}

      {/* Timeline */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Sidebar: nodes + PED units */}
        <Paper sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, bgcolor: THEME.paper, border: `1px solid ${THEME.border}`, borderRadius: 1, p: 1.5 }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: THEME.accent, mb: 1 }}>EXPLOITATION NODES</Typography>
          {orderedNodes.length === 0 && <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>No nodes synced</Typography>}
          {orderedNodes.map((n) => {
            const sel = selectedNode === n._id;
            const subs = allSubnodes(n);
            return (
              <Box
                key={n._id}
                onClick={() => setSelectedNode(sel ? null : n._id)}
                sx={{ p: 0.8, mb: 0.5, borderRadius: 0.5, cursor: "pointer", backgroundColor: sel ? "rgba(0,184,255,0.15)" : "transparent", border: `1px solid ${sel ? THEME.secondary : THEME.border}`, "&:hover": { backgroundColor: "rgba(0,184,255,0.1)" } }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: sel ? THEME.secondary : "#fff" }}>{n.name}</Typography>
                  {n.deleted && <Chip label="retired" size="small" sx={{ height: 14, fontSize: "0.55rem", ml: "auto", backgroundColor: "rgba(255,255,255,0.08)", color: THEME.muted }} />}
                </Box>
                <Typography sx={{ fontSize: "0.62rem", color: THEME.muted }}>
                  {[n.nationality, n.timezoneName, `${subs.length} sub-node${subs.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
            );
          })}
          <Divider sx={{ borderColor: THEME.border, my: 1.5 }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: THEME.accent, mb: 0.5 }}>PED UNITS</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
            {pedUnits.map((u) => (
              <Chip key={u._id} label={u.name} size="small" sx={{ height: 18, fontSize: "0.62rem", backgroundColor: "rgba(255,255,255,0.06)", color: THEME.text }} />
            ))}
          </Box>
        </Paper>

        {/* Grid */}
        <Paper sx={{ flex: 1, minWidth: 0, bgcolor: THEME.paper, border: `1px solid ${THEME.border}`, borderRadius: 1, overflow: "auto" }}>
          {/* Day header */}
          <Box sx={{ display: "flex", borderBottom: `1px solid ${THEME.border}` }}>
            {days.map((d, i) => (
              <Box key={i} sx={{ flex: 1, borderRight: i < DAYS - 1 ? `1px solid ${THEME.border}` : "none" }}>
                <Box sx={{ py: 0.6, textAlign: "center", borderBottom: `1px solid ${THEME.border}` }}>
                  <Typography sx={{ fontSize: "0.72rem", color: THEME.text, fontWeight: 700 }}>{dayLabel(d)}</Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "#666" }}>D+{julian(d)} · {isoDay(d)}</Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  {[0, 6, 12, 18].map((h) => (
                    <Box key={h} sx={{ flex: 1, textAlign: "left", pl: 0.4, fontSize: "0.6rem", color: "#666", py: 0.3, borderRight: "1px solid #222" }}>{String(h).padStart(2, "0")}Z</Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* TEM event bars across the window */}
          {visibleEvents.length > 0 && (
            <Box sx={{ position: "relative", height: 26, borderBottom: `1px solid ${THEME.border}`, backgroundColor: "rgba(247,43,239,0.04)" }}>
              {visibleEvents.map((e) => {
                const s = Math.max(Date.parse(e.start as string), windowStart);
                const t = Math.min(Date.parse(e.stop as string), windowEnd);
                const left = ((s - windowStart) / (windowEnd - windowStart)) * 100;
                const width = Math.max(0.5, ((t - s) / (windowEnd - windowStart)) * 100);
                return (
                  <Tooltip key={e._id} title={`${e.name}: ${shortDate(e.start)} to ${shortDate(e.stop)}`}>
                    <Box sx={{ position: "absolute", top: 4, left: `${left}%`, width: `${width}%`, height: 18, backgroundColor: "rgba(247,43,239,0.3)", border: `1px solid ${THEME.accent}`, borderRadius: 0.5, px: 0.5, overflow: "hidden", whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.62rem", color: "#fff", fontWeight: 700, lineHeight: "16px" }}>{e.name}</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {/* Node rows: one thin availability bar per sub-node, per day */}
          {orderedNodes.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}><Typography sx={{ color: "#666", fontSize: "0.85rem" }}>No exploitation nodes synced</Typography></Box>
          ) : (
            orderedNodes.map((n) => {
              const subs = allSubnodes(n);
              const rowH = Math.max(44, subs.length * 7 + 22);
              const sel = selectedNode === n._id;
              return (
                <Box key={n._id} sx={{ display: "flex", height: rowH, borderBottom: `1px solid ${THEME.border}`, backgroundColor: sel ? "rgba(0,184,255,0.05)" : "transparent", position: "relative" }}>
                  <Typography sx={{ position: "absolute", left: 6, top: 3, fontSize: "0.62rem", color: sel ? THEME.secondary : THEME.muted, fontWeight: 700, zIndex: 1, pointerEvents: "none" }}>
                    {n.name}{n.deleted ? " (retired)" : ""}
                  </Typography>
                  {days.map((d, di) => {
                    const wd = WEEKDAYS[d.getUTCDay()];
                    const covered = subs.some((s) => !!subnodeWindow(s.sub, wd));
                    return (
                      <Box key={di} sx={{ flex: 1, position: "relative", borderRight: di < DAYS - 1 ? `1px solid ${THEME.border}` : "none" }}>
                        {subs.map((s, si) => {
                          const w = subnodeWindow(s.sub, wd);
                          if (!w) return null;
                          const left = (w[0] / 1440) * 100;
                          const width = ((w[1] - w[0]) / 1440) * 100;
                          const color = s.color || THEME.ok;
                          return (
                            <Tooltip key={si} title={`${n.name} sub-node ${si + 1}${s.platform ? " (" + s.platform + ")" : ""}: ${wd} ${hhmm(w[0])}Z to ${hhmm(w[1])}Z`}>
                              <Box sx={{ position: "absolute", top: 18 + si * 7, left: `${left}%`, width: `${width}%`, height: 5, backgroundColor: color, opacity: n.deleted ? 0.35 : 0.85, borderRadius: 0.5 }} />
                            </Tooltip>
                          );
                        })}
                        {subs.length > 0 && !covered && (
                          <Typography sx={{ position: "absolute", left: 6, bottom: 4, fontSize: "0.58rem", color: "#555" }}>no coverage</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })
          )}
          <Box sx={{ px: 1.5, py: 0.6, display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ width: 18, height: 5, backgroundColor: THEME.ok, borderRadius: 0.5 }} />
            <Typography sx={{ fontSize: "0.62rem", color: THEME.muted }}>sub-node exploitation availability (weekly schedule, Zulu) · platform-bound sub-nodes use the platform colour</Typography>
          </Box>
        </Paper>
      </Box>

      {/* Node detail (selected) or all cards */}
      <Box>
        <Typography variant="h6" sx={{ color: "#e0e0e0", fontWeight: "bold", mb: 1.5, fontSize: "1rem" }}>
          EXPLOITATION NODES{detailNode ? ` / ${detailNode.name}` : ""}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: detailNode ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))", gap: 2 }}>
          {(detailNode ? [detailNode] : orderedNodes).map((node) => {
            const it = node.ITResources || {};
            const cap = node.capabilities || {};
            const subs = allSubnodes(node);
            const poc = node.authorityToCommitPOC;
            const pocLine = [poc?.rank, poc?.name, poc?.position].filter(Boolean).join(" · ");
            return (
              <Paper key={node._id} sx={{ bgcolor: THEME.paper, border: `1px solid ${selectedNode === node._id ? THEME.secondary : THEME.border}`, p: 2, borderRadius: 1, cursor: detailNode ? "default" : "pointer" }} onClick={() => !detailNode && setSelectedNode(node._id)}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: "bold", color: THEME.accent }}>{node.name}</Typography>
                  {node.deleted && <Chip label="retired" size="small" sx={{ height: 16, fontSize: "0.58rem", backgroundColor: "rgba(255,255,255,0.08)", color: THEME.muted }} />}
                  <Typography sx={{ fontSize: "0.7rem", color: THEME.muted, ml: "auto" }}>
                    {[node.parentOrg?.name, node.nationality, node.timezoneName].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.68rem", color: THEME.muted }}>
                  Valid {shortDate(node.validDates?.start)} to {shortDate(node.validDates?.end)}
                  {pocLine ? ` · Authority: ${pocLine}` : ""}
                  {node.pointsOfContact?.unclassEmail ? ` · ${node.pointsOfContact.unclassEmail}` : ""}
                  {node.pointsOfContact?.unclassPhone ? ` · ${node.pointsOfContact.unclassPhone}` : ""}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: detailNode ? "repeat(auto-fill, minmax(260px, 1fr))" : "1fr 1fr", columnGap: 2 }}>
                  <Box>
                    {label("Accessible networks")}{chips(it.accessibleNetworks?.map((x) => x.name), THEME.secondary)}
                    {label("Exploitable networks")}{chips(it.exploitableNetworks?.map((x) => x.name), THEME.secondary)}
                    {label("Data formats")}{chips(it.exploitableDataFormats?.map((x) => x.format), THEME.text)}
                    {(it.uploadDownloadImagery || it.canMoveBetweenNetworks !== undefined) && (
                      <>
                        {label("Imagery transfer")}
                        <Typography sx={{ fontSize: "0.7rem", color: THEME.text }}>
                          {it.uploadDownloadImagery || "n/a"}{it.canMoveBetweenNetworks !== undefined ? ` · cross-network: ${it.canMoveBetweenNetworks ? "yes" : "no"}` : ""}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Box>
                    {label("Intel expertise")}{chips(cap.intelExpertise?.map((x) => x.name), THEME.ok)}
                    {label("Geographic expertise")}{chips(cap.geographicalExpertise, THEME.warn)}
                    {label("Sensor categories")}{chips(cap.sensorCategories?.map((x) => `${x.int_discipline}${x.sub_categories?.length ? ": " + x.sub_categories.join("/") : ""}`), THEME.accent)}
                    {label("Exploitation levels")}{chips(cap.exploitationLevels?.map((x) => x.name), THEME.text)}
                    {(cap.operationalRestrictions || cap.technicalRestrictions) && (
                      <>
                        {label("Restrictions")}
                        <Typography sx={{ fontSize: "0.7rem", color: THEME.warn }}>{[cap.operationalRestrictions, cap.technicalRestrictions].filter(Boolean).join(" · ")}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
                {label(`Sub-node collections (${subs.length} sub-nodes)`)}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                  {(cap.subnodeCollections || []).map((c, i) => (
                    <Chip key={i} label={`${c.platform?.name || "Any platform"} x ${(c.subnodes || []).length}`} size="small" sx={{ height: 18, fontSize: "0.62rem", backgroundColor: `${c.platform?.color || THEME.ok}22`, color: c.platform?.color || THEME.ok }} />
                  ))}
                  {(cap.subnodeCollections || []).length === 0 && <Typography sx={{ color: "#555", fontSize: "0.7rem" }}>none</Typography>}
                </Box>
              </Paper>
            );
          })}
        </Box>
        {detailNode && (
          <Button size="small" onClick={() => setSelectedNode(null)} sx={{ mt: 1, color: THEME.secondary, textTransform: "none" }}>Show all nodes</Button>
        )}
      </Box>

      {/* Mission PED taskings (as synced) */}
      {pedTaskings.length > 0 && (
        <Paper sx={{ bgcolor: THEME.paper, border: `1px solid ${THEME.border}`, p: 2, borderRadius: 1 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold", color: "#e0e0e0", mb: 0.5 }}>MISSION PED TASKINGS ({pedTaskings.length})</Typography>
          <Typography sx={{ fontSize: "0.7rem", color: THEME.muted, mb: 1 }}>
            Upstream links from a mission to the collection it exploits. They reference missions by IDEX id; when a referenced mission is outside the synced ATO window, only the ids and any explicit exploitation-time override can be shown.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 0.5 }}>
            {pedTaskings.map((t) => (
              <Box key={t._id} sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: "0.65rem", color: THEME.text, px: 1, py: 0.4, border: `1px solid ${THEME.border}`, borderRadius: 0.5 }}>
                <Typography sx={{ fontSize: "0.65rem", color: THEME.muted, fontFamily: "monospace" }}>mission ..{t.missionId.slice(-6)}</Typography>
                {t.taskings.map((x, i) => (
                  <Typography key={i} sx={{ fontSize: "0.65rem", color: THEME.secondary, fontFamily: "monospace" }}>
                    coll ..{String(x.collectionId || "").slice(-6)}{x.exploitationTimesOverride?.start ? ` ${x.exploitationTimesOverride.start} to ${x.exploitationTimesOverride.stop || ""}` : ""}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
